import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { CONTROL_MIN_HEIGHT } from '@/components/ui/control-size';
import { cn } from '@/lib/utils';

/**
 * The look of the strip's one action, a `Link` or a `button` — the caller
 * renders the element, so a link stays a link. Its height is the shared `sm`
 * control floor (a translated label may wrap), not padding of its own.
 */
export const WARNING_BANNER_ACTION_CLASS = cn(
  'inline-flex shrink-0 items-center justify-center rounded-button bg-primary px-3',
  CONTROL_MIN_HEIGHT.sm,
  'text-xs font-medium text-bg',
  'hover:bg-primary-hover transition-colors disabled:opacity-50',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
);

interface WarningBannerProps {
  icon: LucideIcon;
  message: ReactNode;
  /** The one thing to do about it, styled with `WARNING_BANNER_ACTION_CLASS`. */
  action: ReactNode;
  'data-testid'?: string;
}

/**
 * The shell's warning strip under the header — no records source
 * (`DataSourceBanner`), access not checked (`AccessReadFailedBanner`), the
 * offer to a paused free organization (`PauseOfferBanner`): one shell, so the
 * three never drift apart (code-review #1705, review #1709).
 */
export function WarningBanner({
  icon: Icon,
  message,
  action,
  'data-testid': testId,
}: WarningBannerProps) {
  return (
    <div
      role="status"
      data-testid={testId}
      className={cn(
        'flex items-center justify-between gap-3 border-b border-warning/30',
        'bg-warning-dim px-4 py-2 text-sm'
      )}
    >
      <div className="flex items-center gap-2 text-fg">
        <Icon className="h-4 w-4 text-warning shrink-0" aria-hidden="true" />
        <span>{message}</span>
      </div>
      {action}
    </div>
  );
}
